import {
  ref,
  reactive,
  onBeforeMount,
  getCurrentInstance,
  watch,
  computed,
} from "@vue/composition-api";
import router from "@/router";
import store from "@/store";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import useFilterHeaderTable from "@/views/components/filter-header-table/useFilterHeaderTable";
import usePagination from "@/views/components/pagination/usePagination";
import inventoryListModel from "@/models/inventory/inventory-list";
import flashdealEntryMappingModel from "@/models/flashdeal/flashdeal-entry-mapping";
import flashdealEntryModel from "@/models/flashdeal/flashdeal-entry";

export default function useAddPrivilege(props) {
  const vm = getCurrentInstance().proxy;

  const { fetchInventoryList: fetchInventoryListModel } = inventoryListModel();

  const {
    fetchFlashDealEntryMappingList,
    fetchFlashDealEntryMappingDraft,
  } = flashdealEntryMappingModel();

  const { fetchFlashDealEntryDelete } = flashdealEntryModel();

  const { pagination, onPageChange, onFilterEntity } = usePagination();

  const {
    filters,
    ordering,
    onSetValueSearch,
    onSelectFilter,
  } = useFilterHeaderTable();

  const {
    openDefaultErrorModal,
    openWarningModal,
    openSuccessModal,
  } = useValidationModal();

  const loading = reactive({
    tableData: false,
  });

  const formStatus = reactive({
    validFrom: null,
    validThrough: null,
    status: null,
    inventoryId: null,
    inventoryLabel: null,
  });

  const resultArrPrivilege = ref([]);

  const statusOption = ref([
    { label: "Active", value: 1 },
    { label: "Inactive", value: 2 },
  ]);

  const optionData = reactive({
    inventoryList: [],
  });

  const fields = [
    { key: "checkbox", label: "" },
    { key: "seq", label: "No." },
    { key: "pv_image_url", label: "Image" },
    { key: "pv_name_th", label: "Privilege Name" },
    // { key: 'privilege_type', label: 'Privilege Type' }, // TODO
    { key: "pv_point_ref", label: "Point" },
    { key: "pv_valid_from", label: "Valid Form" },
    { key: "pv_valid_through", label: "Valid Throught" },
    { key: "pv_status", label: "Status" },
    { key: "action", label: "" },
  ];

  const inventoryTable = reactive({
    payload: [],
  });

  const inventorySelect = reactive({
    isSelectAllItem: false,
    selectCurrentInventoryItem: [],
    selectInventoryItem: new Set([]),
  });

  const fetchInventoryOption = (showLoading = false, callBack) => {
    if (showLoading) {
      loading.tableData = true;
    }
    fetchInventoryListModel.payload.Page = pagination.currentPage;
    fetchInventoryListModel.payload.Limit = 10000;
    fetchInventoryListModel.payload.Filters = filters;
    fetchInventoryListModel.payload.Ordering = ordering.value;
    store
      .dispatch("api/apiRequest", fetchInventoryListModel)
      .then((response) => {
        optionData.inventoryList = response.data.payload.map((item) => ({
          ...item,
          label: item.name,
          value: item.alt_reference,
        }));
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
        if (callBack) callBack();
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const setDataToDefault = () => {
    pagination.currentPage = 1;
    inventorySelect.selectCurrentInventoryItem = [];
    inventorySelect.selectInventoryItem = new Set([]);
  };

  const onSelectInventory = (inventoryId) => {
    formStatus.inventoryId = inventoryId;
    setDataToDefault();
    fetchInventoryListTable(true);
  };

  const onSelectAllInventoryItem = (isSelected) => {
    if (isSelected) {
      inventoryTable.payload.map((item) => {
        if (item.fdc_status == null) {
          item.isSelected = true;
        }
      });
    } else {
      inventoryTable.payload.map((item) => {
        if (item.fdc_status == null) {
          item.isSelected = false;
        }
      });
    }
  };

  const resolveStatusLabel = (status) => {
    switch (status) {
      case 1:
        return "Active";
      case 2:
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  const resolveStatusClass = (status) => {
    switch (status) {
      case 1:
        return "active-icon";
      case 2:
        return "inActive-icon";
      default:
        return "Unknown";
    }
  };

  const fetchInventoryListTable = (showLoading = false) => {
    if (showLoading) {
      loading.tableData = true;
    }
    fetchFlashDealEntryMappingList.payload.Campaign_Alt_Ref = router.currentRoute.params.ref;
    fetchFlashDealEntryMappingList.payload.Inventory_Ref =
      formStatus.inventoryId;
    fetchFlashDealEntryMappingList.payload.Page = pagination.currentPage;
    fetchFlashDealEntryMappingList.payload.Limit = pagination.perPage;
    fetchFlashDealEntryMappingList.payload.Filters = filters;
    fetchFlashDealEntryMappingList.payload.Ordering = ordering.value;
    store
      .dispatch("api/apiRequest", fetchFlashDealEntryMappingList)
      .then((response) => {
        inventorySelect.isSelectAllItem = false;
        inventoryTable.payload = response.data.payload.map((o) => ({
          ...o,
          isSelected: o.fdc_status == null ? false : true,
          userInput: {
            newPoint: null,
            issue: {
              overall: null,
              perDay: null,
              perCustomer: null,
              perDayperCustomer: null,
            },
            redeem: {
              overall: null,
              perDay: null,
              perCustomer: null,
              perDayperCustomer: null,
            },
          },
        }));
        pagination.rows = response.data.payload.length;
        pagination.totalRows = response.data.filtered_record;
        getSelectCurrentInventoryItem();
        // if (response.data.payload.length > 0 && isDisplayNonePayload.value) {
        //   isDisplayNonePayload.value = !isDisplayNonePayload.value
        // }
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        loading.tableData = false;
      });
  };

  const getSelectCurrentInventoryItem = () => {
    if (inventorySelect.selectInventoryItem.size == 0) return;

    inventoryTable.payload = inventoryTable.payload.map((o) => ({
      ...o,
      isSelected: inventorySelect.selectInventoryItem.has(o.pv_alt_reference),
    }));

    const isSelectAll =
      pagination.perPage ==
      inventoryTable.payload.filter((o) => o.isSelected === true).length;

    inventorySelect.isSelectAllItem = isSelectAll;
  };

  const onSubmitModal = () => {
    window.$loading(true);
    fetchFlashDealEntryMappingDraft.payload.Campaign_Alt_Ref =
    router.currentRoute.params.ref;
    fetchFlashDealEntryMappingDraft.payload.Inventory_Ref =
      formStatus.inventoryId;
    fetchFlashDealEntryMappingDraft.payload.Privilege_Alt_Ref_List =
      resultArrPrivilege.value;
    store
      .dispatch("api/apiRequest", fetchFlashDealEntryMappingDraft)
      .then((response) => {
        vm.$emit("onNext", {
          inventoryObj: formStatus,
          inventoryTableObj: {
            pagination,
            filters,
            ordering,
          },
        });
      })
      .catch((error) => {
        openDefaultErrorModal(error);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const removeEntry = (entry) => {
    openWarningModal(
      "ลบ Privilege",
      `ต้องการลบ ${entry.pv_name_th} นี้ใช่หรือไม่`,
      () => {
        loading.tableData = true;
        fetchFlashDealEntryDelete.payload.Privilege_Alt_Ref =
          entry.pv_alt_reference;
        fetchFlashDealEntryDelete.payload.Campaign_Alt_Ref = router.currentRoute.params.ref;
        fetchFlashDealEntryDelete.payload.Inventory_Alt_Ref =
          formStatus.inventoryId;
        store
          .dispatch("api/apiRequest", fetchFlashDealEntryDelete)
          .then((response) => {
            openSuccessModal();
          })
          .catch((error) => {
            openDefaultErrorModal(error);
          })
          .finally(() => {
            fetchInventoryListTable();
          });
      }
    );
  };

  const isObjEmpty = (obj) =>
    Object.values(obj).every((x) => x === null || x === "");

  onBeforeMount(() => {
    if (!isObjEmpty(props?.privilegeObj)) {
      const propsData = props.privilegeObj;
      formStatus.status = propsData.inventoryObj.status;
      formStatus.inventoryId = propsData.inventoryObj.inventoryId;
      formStatus.inventoryLabel = propsData.inventoryObj.inventoryLabel;
      pagination.perPageOptions =
        propsData.inventoryTableObj.pagination.perPageOptions;
      pagination.perPage = propsData.inventoryTableObj.pagination.perPage;
      pagination.currentPage =
        propsData.inventoryTableObj.pagination.currentPage;
      pagination.rows = propsData.inventoryTableObj.pagination.rows;
      pagination.totalRows = propsData.inventoryTableObj.pagination.totalRows;
      pagination.totalPage = propsData.inventoryTableObj.pagination.totalPage;
      filters.value = propsData.inventoryTableObj.filters;
      ordering.value = propsData.inventoryTableObj.ordering;

      fetchInventoryOption(true, onSelectInventory(formStatus.inventoryId));
    } else {
      fetchInventoryOption(true);
    }
  });

  const resolveNext = computed(() => {
    return resultArrPrivilege.value.length == 0;
  });

  const storeSelectItemData = () => {
    const isSelectedID = inventoryTable.payload
      .filter((o) => o.isSelected === true && o.fdc_status == null)
      .map((o) => o.pv_alt_reference);

    const isNoSelectedID = inventoryTable.payload
      .filter((o) => !o.isSelected && o.fdc_status == null)
      .map((o) => o.pv_alt_reference);

    if (isSelectedID) {
      isSelectedID.map((pv_alt_reference) => {
        inventorySelect.selectInventoryItem.add(pv_alt_reference);
      });
    }

    if (isNoSelectedID) {
      isNoSelectedID.map((pv_alt_reference) => {
        if (inventorySelect.selectInventoryItem.has(pv_alt_reference))
          inventorySelect.selectInventoryItem.delete(pv_alt_reference);
      });
    }

    inventoryTable.payload
      .filter((o) => o.isSelected === true)
      .map((o) => {
        if (inventorySelect.selectCurrentInventoryItem.length > 0) {
          if (
            inventorySelect.selectCurrentInventoryItem.findIndex(
              (item) => item.pv_alt_reference === o.pv_alt_reference
            ) !== -1
          )
            return;
          inventorySelect.selectCurrentInventoryItem.push(o);
        } else {
          inventorySelect.selectCurrentInventoryItem.push(o);
        }
      });

    resultArrPrivilege.value = [...inventorySelect.selectInventoryItem];
    inventorySelect.selectCurrentInventoryItem = inventorySelect.selectCurrentInventoryItem.filter(
      (item) =>
        resultArrPrivilege.value.some(
          (pv_alt_reference) => item.pv_alt_reference === pv_alt_reference
        )
    );
  };

  watch(
    inventoryTable,
    () => {
      storeSelectItemData();
      vm.$emit("onSelectedCount", inventorySelect.selectInventoryItem.size);
    },
    { deep: true }
  );

  watch(
    pagination,
    () => {
      if (!loading.tableData) {
        storeSelectItemData();
        fetchInventoryListTable(true);
      }
    },
    { deep: true }
  );

  watch(
    [filters, ordering],
    () => {
      pagination.currentPage = 1;
      fetchInventoryListTable(true);
    },
    { deep: true }
  );

  return {
    loading,
    formStatus,
    statusOption,
    optionData,
    pagination,
    fields,
    inventoryTable,
    inventorySelect,
    resolveNext,
    resolveStatusLabel,
    resolveStatusClass,
    onPageChange,
    onFilterEntity,
    onSetValueSearch,
    onSelectFilter,
    onSubmitModal,
    onSelectAllInventoryItem,
    onSelectInventory,
    removeEntry,
  };
}
